import { BaseProduct } from "@utils/product/product-utils-types";
import {
  isProductAllowedFeature,
  isSphereXgProductIdentifier,
} from "@utils/type-guards";
import { SubjectTypeProps } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { DateTimeUtils } from "@stellar/utils";
import { PlanDurationType } from "@utils/plan-duration";

interface Props {
  /** Whether to use the full date time or not */
  shouldShowFullDateTime: boolean;

  /**
   * Whether the plan continues another plan, i.e. it is a copy of it. Such a plan starts at the
   * very moment the other plan ends, so its dates are kept exactly instead of rounded to whole days.
   */
  isContinuingPlan?: boolean;

  /** The ID of the subject that the plan is going to be associated to */
  planSubjectId: string;

  /** The ID of the subscriber that the plan is going to be associated to, it is always the company ID even for project plans */
  planSubscriberId: string;

  /** The selected feature or bundle to add to plan */
  selectedProduct: BaseProduct | null;

  /** Active limits for the plan that is going to be created */
  activeLimits: FaroCoreApiTypes.LimitPayload[];

  /** The features that carry the chosen application settings of the plan */
  appFeatures: FaroCoreApiTypes.FeatureIdentifier[];

  /**
   * Number of SCENE seats that come with the plan. They are limited by a SCENE_SEAT constraint,
   * on top of the SCENE seat plans that are still created for them
   */
  sceneSeatsCount: number;

  /** Start date of the plan */
  startDate: number;

  /** End date of the plan */
  endDate: number;

  /** The unit the duration of the plan was entered in */
  durationType: PlanDurationType;

  /** Comments for the plan that is going to be created */
  comments: string;

  /** Labels for the plan that is going to be created */
  label: string;

  /**
   * The type of the subject that the plan is going to be associated to, e.g. company, project or member
   * COMPANY_MEMBER is excluded because plans cannot be created for company members directly,
   * Currently they are always created for the company or project and then assigned to the member
   */
  subjectType: Exclude<SubjectTypeProps["subjectType"], "COMPANY_MEMBER">;
}

/**
 * @returns the limits of the plan, with the SCENE_SEAT constraint that caps its SCENE seats when it
 * comes with any. Tracking uses it too, so the reported limits match the ones that are created.
 */
export function withSceneSeatLimit(
  activeLimits: FaroCoreApiTypes.LimitPayload[],
  sceneSeatsCount: number
): FaroCoreApiTypes.LimitPayload[] {
  return sceneSeatsCount > 0
    ? [...activeLimits, { type: "SCENE_SEAT", value: sceneSeatsCount }]
    : activeLimits;
}

/** Convert the PlanCreationContext into a FaroCoreApiTypes.CreatePlanPayload object */
export function createPlanCreationPayload({
  shouldShowFullDateTime,
  isContinuingPlan = false,
  planSubjectId,
  planSubscriberId,
  subjectType,
  selectedProduct,
  activeLimits,
  appFeatures,
  sceneSeatsCount,
  startDate,
  endDate,
  durationType,
  label,
  comments,
}: Props): FaroCoreApiTypes.CreatePlanPayload {
  if (!startDate) {
    throw new Error(
      "createPlanCreationPayload: startDate must be set for unpaid plans"
    );
  }
  if (!endDate) {
    throw new Error(
      "createPlanCreationPayload: endDate must be set for unpaid plans"
    );
  }

  const isSphereXgSelected =
    selectedProduct !== null &&
    isSphereXgProductIdentifier(selectedProduct.identifier);

  const planLimits = withSceneSeatLimit(activeLimits, sceneSeatsCount);

  /** For SphereXg plans, the gracePeriodDuration field is set to null to make it a soft limit */
  const limits = isSphereXgSelected
    ? planLimits.map((limit) => {
        return {
          ...limit,
          gracePeriodDuration: null,
        };
      })
    : planLimits;

  const identifiers = selectedProduct?.identifiers ?? [];
  const isFeature =
    selectedProduct && isProductAllowedFeature(selectedProduct.identifier);

  return {
    subject: {
      type: subjectType,
      id:
        // There are two methods for creating device licenses:
        // one where admins provide a specific serial number, and another for any device without a serial number,
        // which uses auto-binding later.For these subscriptions, a special string '??-' is used as the serial number;
        // creating the subscription with subject ID '??-'.
        subjectType === "DEVICE" ? "??-" : planSubjectId,
    },

    limits,
    featureBundles: isFeature ? [] : identifiers,
    features: [...(isFeature ? identifiers : []), ...appFeatures],
    label,
    description: comments,
    startDate: isContinuingPlan
      ? new Date(startDate).toISOString()
      : DateTimeUtils.getDayEdge({
          date: startDate,
          edge: "start",
        }).toISOString(),
    endDate:
      // An unlimited plan ends on a fixed far future date, which is kept exactly as it is
      durationType === "unlimited" || shouldShowFullDateTime || isContinuingPlan
        ? new Date(endDate).toISOString()
        : DateTimeUtils.getDayEdge({
            date: endDate,
            edge: "end",
          }).toISOString(),
    subscriberId: planSubscriberId,
  };
}
